import { memo } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { LoadingButton } from "@mui/lab";
import { Box, Typography, Alert, Divider } from "@mui/material";
import { Icon } from '@iconify/react';

function MuiConfirmationDialog(props) {
    const { 
        title,
        contentText,
        content,
        children,
        closeText,
        okText,
        onOk,
        isLoading,
        severity = "warning",
        showIcon = true,
        icon,
        maxWidth = 'sm',
        fullWidth = true,
        showDivider = false,
        customActions,
        showCancel = true,
        okButtonProps = {},
        cancelButtonProps = {},
        contentProps = {},
        ...rest
     } = props;

    const getSeverityIcon = (severity) => {
        switch (severity) {
            case 'error':
                return 'eva:alert-triangle-fill';
            case 'warning':
                return 'eva:alert-circle-fill';
            case 'info':
                return 'eva:info-fill';
            case 'success':
                return 'eva:checkmark-circle-2-fill';
            default:
                return 'eva:help-circle-fill';
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'error':
                return 'error';
            case 'warning':
                return 'warning';
            case 'info':
                return 'info';
            case 'success':
                return 'success';
            default:
                return 'primary';
        }
    };

    return (
        <Dialog
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            {...rest}
        >
            <DialogTitle 
                id="alert-dialog-title"
                sx={{ 
                    pb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}
            >
                {showIcon && (
                    <Icon 
                        icon={icon || getSeverityIcon(severity)}
                        style={{ 
                            fontSize: '24px',
                            color: severity === 'warning' ? '#ed6c02' : undefined
                        }}
                    />
                )}
                {title}
            </DialogTitle>

            {showDivider && <Divider />}

            <DialogContent {...contentProps}>
                {/* Alert Banner */}
                {severity && (
                    <Alert 
                        severity={severity} 
                        sx={{ mb: 2 }}
                        icon={false}
                    >
                        {contentText}
                    </Alert>
                )}

                {/* Custom Content */}
                {content && (
                    <Box sx={{ mb: 2 }}>
                        {content}
                    </Box>
                )}

                {/* Children Content */}
                {children && (
                    <Box>
                        {children}
                    </Box>
                )}

                {/* Fallback Content Text */}
                {!content && !children && contentText && (
                    <DialogContentText>
                        {contentText}
                    </DialogContentText>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                {showCancel && (
                    <Button 
                        variant="outlined" 
                        size="large" 
                        onClick={rest.onClose}
                        disabled={isLoading}
                        {...cancelButtonProps}
                    >
                        {closeText}
                    </Button>
                )}
                
                {customActions ? (
                    customActions
                ) : (
                    <LoadingButton 
                        type="button" 
                        variant="contained" 
                        size="large" 
                        onClick={onOk} 
                        loading={isLoading}
                        color={getSeverityColor(severity)}
                        {...okButtonProps}
                    >
                        {okText}
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default memo(MuiConfirmationDialog);